import type { RiskLevel, SiteAnalysisResponse } from '@/lib/api.types';
import type { StatusKind } from '@/components/ui/StatusRow/StatusRow';

// Single source of truth for how a risk rank becomes a UI status / label, and how
// the four risk-bearing portfolios combine into one overall rank. Shared by the
// top-of-report VerdictCard, ChapterOverview, and the chapter StatusRows (the
// chapters/shared.tsx re-export keeps existing imports working).

export const RANK_ORDER: Record<RiskLevel, number> = {
  unknown: 0,
  low: 1,
  medium: 2,
  high: 3,
  very_high: 4,
};

export function rankToStatus(rank: RiskLevel): StatusKind {
  switch (rank) {
    case 'low':
      return 'safe';
    case 'medium':
    case 'high':
    case 'very_high':
      return 'alert';
    default:
      return 'neutral';
  }
}

export function rankToStatusLabel(rank: RiskLevel, fallback = '判定中'): string {
  switch (rank) {
    case 'low':
      return '該当なし';
    case 'medium':
      return '要確認';
    case 'high':
    case 'very_high':
      return '要注意';
    default:
      return fallback;
  }
}

export function maxRank(ranks: readonly RiskLevel[]): RiskLevel {
  return ranks.reduce<RiskLevel>(
    (acc, r) => (RANK_ORDER[r] > RANK_ORDER[acc] ? r : acc),
    'unknown',
  );
}

export type RiskDimension = {
  key: 'hazards' | 'ground' | 'planning' | 'life';
  label: string;
  rank: RiskLevel;
};

// The four portfolios that carry a meaningful risk rank, in the order shown on
// the verdict card. Price is intentionally excluded — it is a market figure, not
// a safety risk, and it does not feed the overall judgement.
export function riskDimensions(analysis: SiteAnalysisResponse): RiskDimension[] {
  return [
    { key: 'hazards', label: '災害', rank: analysis.hazards.rank },
    { key: 'ground', label: '地盤', rank: analysis.ground.rank },
    { key: 'planning', label: '建築', rank: analysis.planning.rank },
    { key: 'life', label: '暮らし', rank: analysis.life_environment.rank },
  ];
}

export function overallRank(analysis: SiteAnalysisResponse): RiskLevel {
  return maxRank(riskDimensions(analysis).map((d) => d.rank));
}
